import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import { UIProvider, useUI } from './context/UIContext';
import { TutorialProvider } from './context/TutorialContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ChefsLabProvider } from './context/ChefsLabContext';
import { AudioSettingsProvider, useAudioSettings } from './context/AudioSettingsContext';
import { supabase } from './lib/supabase';
import { isGuest } from './lib/guestMode';
import { GuestPromptProvider } from './context/GuestPromptContext';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Camera } from '@capacitor/camera';
import CameraService from './services/CameraService';
import { initializeOneSignalSDK, registerDeviceForUser } from './services/onesignal';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import GameScreen from './pages/GameScreen';
import Home from './pages/Home';
import Collection from './pages/Collection';
import CardDetail from './pages/CardDetail';
import ChefsLab from './pages/ChefsLab';
import ChefsLabCompare from './pages/ChefsLabCompare';
import MeetUp from './pages/MeetUp';
import Messages from './pages/meetup/Messages';
import Requests from './pages/meetup/Requests';
import Trades from './pages/meetup/Trades';
import TradeDetail from './pages/meetup/TradeDetail';
import TradeReview from './pages/meetup/TradeReview';
import Chat from './pages/meetup/Chat';
import Locations from './pages/Locations';
import LocationDetail from './pages/LocationDetail';
import LocationManagement from './pages/LocationManagement';
import Profile from './pages/Profile';
import Preferences from './pages/Preferences';
import PricingPreferences from './pages/PricingPreferences';
import NotificationSettings from './pages/NotificationSettings';
import ManageFollowing from './pages/ManageFollowing';
import ProfilePicker from './pages/ProfilePicker';
import ParentQuickSetup from './pages/ParentQuickSetup';
import ParentSignup from './pages/ParentSignup';
import SetupLogin from './pages/SetupLogin';
import Scanner from './pages/Scanner.legacy';
import BulkScanner from './pages/BulkScanner';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import CardResearch from './pages/CardResearch';
import FairTrade from './pages/FairTrade';
import AdminReports from './pages/AdminReports';
import AdminLocationClaims from './pages/AdminLocationClaims';
import AdminLocationDisputes from './pages/AdminLocationDisputes';
import LocationPartnerAgreement from './pages/legal/LocationPartnerAgreement';
import MyStorePortal from './pages/MyStorePortal';
import BottomNav from './components/BottomNav';
import BadgeCongrats from './components/BadgeCongrats';
import PostModalWrapper from './components/PostModalWrapper';
import PageTransition from './components/PageTransition';
import PendingReviewModal from './components/PendingReviewModal';
import cardChaseLogo from './assets/logo.png';
import './App.css';

// App shell wrapper - fixed shell with scrollable content area
const AppShell = ({ children, showNav = false }) => {
  const location = useLocation();

  return (
    <div className="app-shell">
      <div className="app-content">
        <AnimatePresence mode="wait" initial={false}>
          <PageTransition key={location.pathname}>
            {children}
          </PageTransition>
        </AnimatePresence>
      </div>
      {showNav && <BottomNav />}
      {showNav && <PostModalWrapper />}
      <BadgeCongrats />
    </div>
  );
};

// Loading spinner component
// Loading state shown while AuthContext resolves the cached session and
// useOnboardingCheck queries the DB to decide where to route (/home vs /game
// vs /parent-quick-setup vs /profile-picker). Replaces the previous spinner
// with the CardChase logo + calm pulse so the wait feels branded, not anxious.
const LoadingScreen = () => {
  const { theme } = useTheme();
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: theme.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}>
      <style>{`
        @keyframes loading-logo-pulse {
          0%, 100% { transform: scale(1); opacity: 0.78; }
          50%      { transform: scale(1.05); opacity: 1; }
        }
        @keyframes loading-logo-in {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <img
        src={cardChaseLogo}
        alt=""
        aria-hidden="true"
        style={{
          width: 120,
          height: 120,
          borderRadius: 24,
          objectFit: 'cover',
          animation:
            'loading-logo-in 400ms ease-out both, ' +
            'loading-logo-pulse 1600ms ease-in-out 400ms infinite',
        }}
      />
    </div>
  );
};

// Module-level cache so multiple route guards using useOnboardingCheck don't
// each kick off their own DB query (and their own LoadingScreen flash) on every
// navigation. Cleared on signout via the useEffect below.
const onboardingCache = {
  userId: null,            // the supabase auth user id this cache is for
  needsOnboarding: null,
  userType: null,
};

// Check if user has completed onboarding and get user type
const useOnboardingCheck = () => {
  const { user, loading: authLoading } = useAuth();

  // Lazy init: if we already resolved this exact user, start with the cached
  // values + loading=false so no LoadingScreen frame renders.
  const hasCacheForUser = user && onboardingCache.userId === user.id;
  const [needsOnboarding, setNeedsOnboarding] = useState(() =>
    hasCacheForUser ? onboardingCache.needsOnboarding : null
  );
  const [userType, setUserType] = useState(() =>
    hasCacheForUser ? onboardingCache.userType : null
  );
  const [loading, setLoading] = useState(() => !hasCacheForUser);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (authLoading) return;

      if (!user) {
        // Clear cache on signout so a future signin doesn't reuse stale data.
        onboardingCache.userId = null;
        onboardingCache.needsOnboarding = null;
        onboardingCache.userType = null;
        setNeedsOnboarding(false);
        setUserType(null);
        setLoading(false);
        return;
      }

      // Cache hit — skip the DB call.
      if (onboardingCache.userId === user.id) {
        setNeedsOnboarding(onboardingCache.needsOnboarding);
        setUserType(onboardingCache.userType);
        setLoading(false);
        return;
      }

      try {
        // Fetch user data including parent/child fields
        const { data, error } = await supabase
          .from('users')
          .select('id, intake_completed, first_name, username, is_parent, parent_will_trade, is_under_18, default_profile_id, pref_skip_title_screen')
          .eq('auth_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking onboarding:', error);
        }

        const needsIt = !data || !data.intake_completed || !data.first_name || !data.username;
        setNeedsOnboarding(needsIt);

        let nextUserType = null;
        if (data) {
          // Determine user type for routing
          nextUserType = {
            userId: data.id,
            isParent: data.is_parent || false,
            parentWillTrade: data.parent_will_trade || false,
            isUnder18: data.is_under_18 || false,
            defaultProfileId: data.default_profile_id,
            prefSkipTitleScreen: data.pref_skip_title_screen === true,
          };
          setUserType(nextUserType);

          if (data.pref_skip_title_screen === true) {
            try { localStorage.setItem('skipTitleScreen', 'true'); } catch (_) {}
          }
        }

        // Populate cache so the next route guard for this same user reuses
        // this result without re-querying and re-flashing the LoadingScreen.
        onboardingCache.userId = user.id;
        onboardingCache.needsOnboarding = needsIt;
        onboardingCache.userType = nextUserType;
      } catch (err) {
        console.error('Error:', err);
        setNeedsOnboarding(true);
      } finally {
        setLoading(false);
      }
    };

    checkOnboarding();
  }, [user, authLoading]);

  return { needsOnboarding, userType, loading: loading || authLoading };
};

// Badge count fetcher - runs on auth and updates global count
const BadgeCountFetcher = () => {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { setChatBadgeCount } = useUI();

  useEffect(() => {
    const fetchBadgeCounts = async () => {
      if (!user || !activeProfile?.id) {
        setChatBadgeCount(0);
        return;
      }

      try {
        // Use activeProfile.id directly (handles parent viewing as child)
        const userId = activeProfile.id;

        // Fetch unseen requests count
        const { data: unseenRequests } = await supabase
          .from('trade_requests')
          .select('id, requester_id')
          .eq('card_owner_id', userId)
          .eq('status', 'pending')
          .eq('viewed_by_owner', false);

        const uniqueRequesters = new Set(unseenRequests?.map(r => r.requester_id) || []);
        const unseenCount = uniqueRequesters.size;

        // Fetch unread messages count
        const { data: activeChats } = await supabase
          .from('trade_requests')
          .select('id, requester_id, card_owner_id, last_read_requester_at, last_read_owner_at, trade_schedules (status)')
          .or(`requester_id.eq.${userId},card_owner_id.eq.${userId}`)
          .eq('status', 'accepted');

        // Count unread messages across all active chats (confirmed and unconfirmed)
        let totalUnread = 0;
        for (const chat of (activeChats || [])) {
          const isRequester = chat.requester_id === userId;
          const lastReadAt = isRequester ? chat.last_read_requester_at : chat.last_read_owner_at;

          let unreadQuery = supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('trade_request_id', chat.id)
            .eq('is_ignored', false)
            .neq('sender_id', userId);

          if (lastReadAt) {
            unreadQuery = unreadQuery.gt('created_at', lastReadAt);
          }

          const { count } = await unreadQuery;
          totalUnread += count || 0;
        }

        setChatBadgeCount(totalUnread + unseenCount);
      } catch (error) {
        console.error('Error fetching badge counts:', error);
      }
    };

    fetchBadgeCounts();

    // Refresh every 30 seconds
    const interval = setInterval(fetchBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, [user, activeProfile?.id, setChatBadgeCount]);

  return null; // This component renders nothing
};

// Global Background Music - persists across all screens EXCEPT game/auth screens
// Only plays when user is on Home or other main app screens
// Reads music/volume preferences from AudioSettingsContext (synced to localStorage)
// Pauses when app goes to background to prevent iOS issues
const BackgroundMusic = () => {
  const location = useLocation();
  const bgMusicRef = useRef(null);
  const { settings } = useAudioSettings();
  const [localMusicEnabled, setLocalMusicEnabled] = useState(() => {
    return localStorage.getItem('musicEnabled') !== 'false';
  });
  const [isAppActive, setIsAppActive] = useState(true);

  // Sync from context (covers logged-in users) and localStorage (covers pre-login)
  const musicEnabled = settings.musicEnabled !== undefined ? settings.musicEnabled : localMusicEnabled;
  const mainVolume = settings.mainVolume !== undefined ? settings.mainVolume : 0.8;

  // Routes where music should NOT play
  const silentRoutes = ['/game', '/login', '/signup', '/parent-signup', '/parent-quick-setup', '/setup-login', '/', '/my-store', '/locations', '/location-management', '/admin/location-claims', '/admin/location-disputes', '/legal/location-partner-agreement'];
  const shouldPlayMusic = !silentRoutes.some(route =>
    location.pathname === route || location.pathname.startsWith('/game') || location.pathname.startsWith('/location') || location.pathname.startsWith('/admin/location') || location.pathname.startsWith('/legal/')
  );

  // Listen for app state changes (foreground/background)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handleStateChange = (state) => {
      setIsAppActive(state.isActive);
    };

    CapacitorApp.addListener('appStateChange', handleStateChange);

    return () => {};
  }, []);

  // Listen for localStorage changes (fallback for pre-login and cross-tab)
  useEffect(() => {
    const checkMusicSetting = () => {
      const enabled = localStorage.getItem('musicEnabled') !== 'false';
      setLocalMusicEnabled(enabled);
    };

    window.addEventListener('storage', checkMusicSetting);
    const interval = setInterval(checkMusicSetting, 2000);

    return () => {
      window.removeEventListener('storage', checkMusicSetting);
      clearInterval(interval);
    };
  }, []);

  // Apply volume setting
  useEffect(() => {
    if (bgMusicRef.current) {
      bgMusicRef.current.volume = mainVolume;
    }
  }, [mainVolume]);

  // Play/pause music based on setting, app state, AND current route
  useEffect(() => {
    if (bgMusicRef.current) {
      if (musicEnabled && isAppActive && shouldPlayMusic) {
        bgMusicRef.current.play().catch(() => {});
      } else {
        bgMusicRef.current.pause();
      }
    }
  }, [musicEnabled, isAppActive, shouldPlayMusic]);

  // Handle user interaction to start music (for autoplay policy)
  useEffect(() => {
    const handleInteraction = () => {
      if (bgMusicRef.current && musicEnabled && isAppActive && shouldPlayMusic) {
        bgMusicRef.current.play().catch(() => {});
      }
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('touchstart', handleInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, [musicEnabled, isAppActive, shouldPlayMusic]);

  return (
    <audio
      ref={bgMusicRef}
      src={process.env.PUBLIC_URL + '/music/PokemonCenter.mp3'}
      loop
      style={{ display: 'none' }}
    />
  );
};

// Camera Pre-Warming - initializes camera service on app start
// ONLY if permission is already granted (don't trigger permission dialog)
const CameraInitializer = () => {
  useEffect(() => {
    // Only initialize on native platforms
    if (!Capacitor.isNativePlatform()) return;

    const checkAndPrewarm = async () => {
      try {
        // Check if camera permission is already granted
        const permissions = await Camera.checkPermissions();
        console.log('[CameraService] Permission status:', permissions.camera);

        if (permissions.camera === 'granted') {
          // Permission already granted - safe to pre-warm
          console.log('[PERF] Pre-warming camera service...');
          CameraService.initialize().catch(err => {
            console.log('[CameraService] Pre-warm failed (will retry on Scanner open):', err.message);
          });
        } else {
          console.log('[CameraService] Permission not granted yet, skipping pre-warm (will request in onboarding)');
        }
      } catch (err) {
        console.log('[CameraService] Permission check failed:', err.message);
      }
    };

    // Pre-warm camera after a short delay (let app finish loading first)
    const timer = setTimeout(checkAndPrewarm, 1000);

    return () => clearTimeout(timer);
  }, []);

  return null;
};

// Permission Handler for Returning Users
// If user has completed onboarding but permissions aren't granted on this device, request them
const ReturningUserPermissions = () => {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user || hasChecked) return;

    const checkAndRequestPermissions = async () => {
      try {
        // Check if user has completed onboarding
        const { data: userData } = await supabase
          .from('users')
          .select('intake_completed, id')
          .eq('auth_id', user.id)
          .single();

        if (!userData?.intake_completed) {
          // New user - onboarding will handle permissions
          console.log('[Permissions] New user, onboarding will handle permissions');
          setHasChecked(true);
          return;
        }

        // Returning user — check, but do NOT proactively request.
        // Apple expects permission prompts to be tied to user intent. The
        // Scanner page itself requests camera permission when the user taps
        // Scan; firing iOS's system prompt here (on Home, with no UI context)
        // is the kind of just-in-time-violation that gets flagged on review.
        const cameraPerms = await Camera.checkPermissions();
        console.log('[Permissions] Camera state on Home:', cameraPerms.camera);

        // Notification permissions handled by PushNotificationPrompt banner on Home screen.
        setHasChecked(true);
      } catch (err) {
        console.log('[Permissions] Error checking/requesting permissions:', err);
        setHasChecked(true);
      }
    };

    // Small delay to let app stabilize
    const timer = setTimeout(checkAndRequestPermissions, 2000);
    return () => clearTimeout(timer);
  }, [user, hasChecked]);

  return null;
};

// Device Registration - uses OneSignal SDK with subscription ID
const DeviceRegistration = () => {
  const { user } = useAuth();
  const { activeProfile } = useProfile();

  // Initialize SDK on mount
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    initializeOneSignalSDK();
  }, []);

  // Register device for ALL profiles on this account (parent + all children)
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user) return;

    const registerAllProfiles = async () => {
      try {
        // Get the parent user (the one who authenticated)
        const { data: parentUser } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (!parentUser) return;

        // Get all children linked to this parent
        const { data: childLinks } = await supabase
          .from('family_links')
          .select('child_id')
          .eq('parent_id', parentUser.id);

        // Build list of all user IDs to register (parent + all children)
        const allUserIds = [parentUser.id];
        if (childLinks) {
          childLinks.forEach(link => allUserIds.push(link.child_id));
        }

        // Register each profile
        for (const userId of allUserIds) {
          await registerDeviceForUser(userId);
        }

        console.log('[OneSignal] Registered', allUserIds.length, 'profiles');
      } catch (err) {
        console.error('[OneSignal] Registration failed:', err);
      }
    };

    registerAllProfiles();
  }, [user, activeProfile?.id]);

  return null;
};

// Deep Link Handler - navigates to route from notification click or URL scheme
const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Handle cardchase:// URL scheme deep links (from email invites)
  useEffect(() => {
    // Set up listener for URL scheme deep links
    const setupUrlListener = async () => {
      // Only run on native platforms
      if (!Capacitor.isNativePlatform()) return;

      // Handle URLs that opened the app
      CapacitorApp.addListener('appUrlOpen', ({ url }) => {
        console.log('[DeepLink] App opened with URL:', url);

        // Parse the cardchase:// URL
        if (url.startsWith('cardchase://')) {
          const path = url.replace('cardchase://', '');
          const [route, queryString] = path.split('?');

          // Build the web route with query params
          let webRoute = '/' + route;
          if (queryString) {
            webRoute += '?' + queryString;
          }

          console.log('[DeepLink] Navigating to:', webRoute);
          navigate(webRoute);
        }
      });

      // Check if app was opened from a URL (cold start)
      const launchUrl = await CapacitorApp.getLaunchUrl();
      if (launchUrl?.url?.startsWith('cardchase://')) {
        console.log('[DeepLink] Cold start with URL:', launchUrl.url);
        const path = launchUrl.url.replace('cardchase://', '');
        const [route, queryString] = path.split('?');
        let webRoute = '/' + route;
        if (queryString) {
          webRoute += '?' + queryString;
        }
        navigate(webRoute);
      }
    };

    setupUrlListener();

    // Note: Not removing listeners on cleanup because removeAllListeners()
    // would also remove appStateChange listener used by BackgroundMusic.
    // The appUrlOpen listener is harmless to keep around.
  }, [navigate]);

  // Handle OneSignal notification deep links (requires auth)
  useEffect(() => {
    if (!user) return;

    // Check for pending deep link from notification click
    const pending = localStorage.getItem('pendingDeepLink');
    if (pending) {
      try {
        const data = JSON.parse(pending);
        localStorage.removeItem('pendingDeepLink');

        // Route based on notification type
        const { type, tradeId, cardId, userId } = data;
        switch (type) {
          case 'trade_request':
            navigate('/requests');
            break;
          case 'trade_accepted':
          case 'new_message':
            if (tradeId) navigate(`/chat/${tradeId}`);
            break;
          case 'post_reaction':
            if (cardId) navigate(`/card/${cardId}`);
            break;
          case 'followed_post':
            // Navigate to home with post filter
            if (data.postId) {
              navigate(`/home?postId=${data.postId}`);
            } else if (cardId) {
              navigate(`/card/${cardId}`);
            }
            break;
          case 'new_follower':
            if (userId) navigate(`/user/${userId}/collection`);
            break;
          case 'trade_scheduled':
          case 'trade_reminder_1day':
          case 'trade_reminder_today':
            if (tradeId) navigate(`/chat/${tradeId}`);
            break;
          case 'review_prompt':
            if (tradeId) navigate(`/trade/${tradeId}/review`);
            break;
          case 'morning_digest':
            navigate('/messages');
            break;
          case 'security_alert':
          case 'milestone':
          case 'rating_warning':
            navigate('/profile');
            break;
          default:
            navigate('/home');
        }
      } catch (err) {
        console.error('[DeepLink] Parse error:', err);
        localStorage.removeItem('pendingDeepLink');
      }
    }
  }, [user, navigate]);

  return null;
};

// Protected Route - redirects to login if not authenticated
const ProtectedRoute = ({ children, allowParentDashboardOnly = false }) => {
  const { user, loading: authLoading } = useAuth();
  const { activeProfileId } = useProfile();
  const { needsOnboarding, userType, loading: onboardingLoading } = useOnboardingCheck();

  if (authLoading || onboardingLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to appropriate onboarding if needed
  if (needsOnboarding) {
    const meta = user?.user_metadata;
    if (meta?.signup_type === 'parent' && !meta?.parent_will_trade) {
      return <Navigate to="/parent-quick-setup" replace />;
    }
    return <Navigate to="/game" replace />;
  }

  // Handle parent who doesn't trade - redirect to profile picker to choose a profile
  // Check both database defaultProfileId AND context activeProfileId (for immediate navigation)
  const hasProfileSelected = userType?.defaultProfileId || activeProfileId;
  if (userType?.isParent && !userType?.parentWillTrade && !hasProfileSelected && !allowParentDashboardOnly) {
    return <Navigate to="/profile-picker" replace />;
  }

  return children;
};

// Game Route - needs auth, handles title/options/onboarding internally
const GameRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Guest Route - allows render if user authenticated OR guest mode is enabled.
// Used for routes that Apple 5.1.1(v) requires be browsable without an account
// (Home feed, Locations, Card detail). Pages still hide write actions for guests.
const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return children;
  if (isGuest()) return children;
  return <Navigate to="/login" replace />;
};

// Public Route - if already authenticated, defer routing to RootRedirect
// (which knows about DB prefs like prefSkipTitleScreen + parent status).
// This avoids a "fresh device" bug where localStorage was empty and the
// authenticated user was forced through the title screen even though their
// DB preference said to skip it.
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Root redirect - routes based on user type
const RootRedirect = () => {
  const { user, loading: authLoading } = useAuth();
  const { activeProfileId } = useProfile();
  const { needsOnboarding, userType, loading: onboardingLoading } = useOnboardingCheck();

  if (authLoading || onboardingLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    if (isGuest()) return <Navigate to="/home" replace />;
    return <Navigate to="/login" replace />;
  }

  // Needs onboarding - route based on signup metadata
  if (needsOnboarding) {
    const meta = user?.user_metadata;
    if (meta?.signup_type === 'parent' && !meta?.parent_will_trade) {
      return <Navigate to="/parent-quick-setup" replace />;
    }
    return <Navigate to="/game" replace />;
  }

  // Route based on user type:
  // - is_parent + parent_will_trade -> Home as parent (default; switch profiles via Profile -> Parental Controls)
  // - is_parent + NOT will_trade + no default profile -> Profile Picker (must pick a kid to view as)
  // - is_under_18 -> Home (viewer-only handled in components)
  // - Adult (non-parent) -> Home

  const hasProfileSelected = userType?.defaultProfileId || activeProfileId;
  if (userType?.isParent && !userType?.parentWillTrade && !hasProfileSelected) {
    return <Navigate to="/profile-picker" replace />;
  }

  // Check skip title preference (DB flag OR localStorage)
  const skipTitle = userType?.prefSkipTitleScreen || localStorage.getItem('skipTitleScreen') === 'true';
  if (skipTitle) {
    return <Navigate to="/home" replace />;
  }

  return <Navigate to="/game" replace />;
};

function App() {
  return (
    <ThemeProvider>
    <BrowserRouter>
      <UIProvider>
        <AuthProvider>
          <GuestPromptProvider>
          <ProfileProvider>
          <AudioSettingsProvider>
          <ChefsLabProvider>
          <TutorialProvider>
          {/* Hidden container for camera pre-warming */}
          <div id="camera-service-container" style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
          <BackgroundMusic />
          <CameraInitializer />
          <BadgeCountFetcher />
          <DeviceRegistration />
          <ReturningUserPermissions />
          <DeepLinkHandler />
          <PendingReviewModal />
          <Routes>
          {/* Root - redirects based on auth state */}
          <Route path="/" element={<RootRedirect />} />

          {/* Game Screen - unified GameBoy experience (title, options, onboarding) */}
          <Route
            path="/game"
            element={
              <GameRoute>
                <GameScreen />
              </GameRoute>
            }
          />

          {/* Home - public feed; guests may browse, write actions hidden */}
          <Route
            path="/home"
            element={
              <GuestRoute>
                <AppShell showNav>
                  <Home />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Cards - protected (my collection) */}
          <Route
            path="/cards"
            element={
              <GuestRoute>
                <AppShell showNav>
                  <Collection />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Scanner - full screen camera (no nav) */}
          <Route
            path="/scanner"
            element={
              <ProtectedRoute>
                <Scanner />
              </ProtectedRoute>
            }
          />

          {/* Bulk Scanner - rapid-fire multi-card scanning (no nav) */}
          <Route
            path="/bulk-scanner"
            element={
              <ProtectedRoute>
                <BulkScanner />
              </ProtectedRoute>
            }
          />

          {/* Card Research Lab - state-driven manual card addition (no nav) */}
          <Route
            path="/add-card-manual"
            element={
              <ProtectedRoute>
                <CardResearch />
              </ProtectedRoute>
            }
          />

          {/* User Collection - browsable by guests (view another user's collection) */}
          <Route
            path="/user/:userId/collection"
            element={
              <GuestRoute>
                <AppShell showNav>
                  <Collection />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Card Detail - browsable by guests (read-only) */}
          <Route
            path="/card/:cardId"
            element={
              <GuestRoute>
                <AppShell>
                  <CardDetail />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Chef's Lab - grading comparison tool */}
          <Route
            path="/chefs-lab/:cardId"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ChefsLab />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Chef's Lab Demo Mode - standalone card selector */}
          <Route
            path="/chefs-lab"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ChefsLab />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Chef's Lab Compare - company rankings */}
          <Route
            path="/chefs-lab/compare"
            element={
              <ProtectedRoute>
                <AppShell>
                  <ChefsLabCompare />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Fair Trade - full screen trade calculator (no nav) */}
          <Route
            path="/fair-trade"
            element={
              <ProtectedRoute>
                <FairTrade />
              </ProtectedRoute>
            }
          />


          {/* Locations - browsable by guests (verified shops list/map) */}
          <Route
            path="/locations"
            element={
              <GuestRoute>
                <AppShell showNav>
                  <Locations />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Location Detail - browsable by guests (verified shop info) */}
          <Route
            path="/location/:locationId"
            element={
              <GuestRoute>
                <AppShell>
                  <LocationDetail />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Location Management - for store owners/staff */}
          <Route
            path="/location-management"
            element={
              <ProtectedRoute>
                <AppShell>
                  <LocationManagement />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Chat/MeetUp Hub - browsable by guests; page shows empty + sign-in prompt */}
          <Route
            path="/chat"
            element={
              <GuestRoute>
                <AppShell showNav>
                  <MeetUp />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Messages - urgency filtered list */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <AppShell showNav>
                  <Messages />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Trade Requests - incoming/outgoing */}
          <Route
            path="/requests"
            element={
              <ProtectedRoute>
                <AppShell showNav>
                  <Requests />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Trades - scheduled meetups */}
          <Route
            path="/trades"
            element={
              <ProtectedRoute>
                <AppShell showNav>
                  <Trades />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Trade Detail - single trade view */}
          <Route
            path="/trade/:tradeId"
            element={
              <ProtectedRoute>
                <AppShell>
                  <TradeDetail />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Trade Review - rate trader and location */}
          <Route
            path="/trade/:id/review"
            element={
              <ProtectedRoute>
                <AppShell>
                  <TradeReview />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Chat with specific trade */}
          <Route
            path="/chat/:tradeId"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Chat />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Profile - browsable by guests; page shows sign-in prompt */}
          <Route
            path="/profile"
            element={
              <GuestRoute>
                <AppShell showNav>
                  <Profile />
                </AppShell>
              </GuestRoute>
            }
          />

          {/* Preferences - protected */}
          <Route
            path="/preferences"
            element={
              <ProtectedRoute>
                <AppShell>
                  <Preferences />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Pricing Preferences - protected */}
          <Route
            path="/pricing-preferences"
            element={
              <ProtectedRoute>
                <AppShell>
                  <PricingPreferences />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Admin Reports - protected */}
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AdminReports />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Admin Location Claims - protected */}
          <Route
            path="/admin/location-claims"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AdminLocationClaims />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Admin Location Disputes - protected */}
          <Route
            path="/admin/location-disputes"
            element={
              <ProtectedRoute>
                <AppShell>
                  <AdminLocationDisputes />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* My Store - Location Partner Portal (public, self-contained auth) */}
          <Route
            path="/my-store"
            element={
              <AppShell>
                <MyStorePortal />
              </AppShell>
            }
          />

          {/* Location Partner Agreement */}
          <Route
            path="/legal/location-partner-agreement"
            element={
              <AppShell>
                <LocationPartnerAgreement />
              </AppShell>
            }
          />

          {/* Notification Settings - protected */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <AppShell>
                  <NotificationSettings />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Parent Quick Setup - non-trading parent onboarding (no GameBoy) */}
          <Route
            path="/parent-quick-setup"
            element={
              <GameRoute>
                <AppShell>
                  <ParentQuickSetup />
                </AppShell>
              </GameRoute>
            }
          />

          {/* Profile Picker - for all parents to choose/manage profiles */}
          <Route
            path="/profile-picker"
            element={
              <ProtectedRoute allowParentDashboardOnly>
                <AppShell>
                  <ProfilePicker />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Parent Signup - public (from email invite link) */}
          <Route
            path="/parent-signup"
            element={
              <AppShell>
                <ParentSignup />
              </AppShell>
            }
          />

          {/* Setup Login - public (child sets password from email link) */}
          <Route
            path="/setup-login"
            element={
              <AppShell>
                <SetupLogin />
              </AppShell>
            }
          />

          {/* Manage Following - protected */}
          <Route
            path="/following"
            element={
              <ProtectedRoute>
                <AppShell showNav>
                  <ManageFollowing />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Post - protected */}
          <Route
            path="/post"
            element={
              <ProtectedRoute>
                <AppShell showNav>
                  <Home />
                </AppShell>
              </ProtectedRoute>
            }
          />

          {/* Login - public only */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <AppShell>
                  <Login />
                </AppShell>
              </PublicRoute>
            }
          />

          {/* Signup - public only */}
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <AppShell>
                  <SignUp />
                </AppShell>
              </PublicRoute>
            }
          />

          {/* Legal pages - public, no auth required */}
          <Route
            path="/terms"
            element={
              <AppShell>
                <Terms />
              </AppShell>
            }
          />
          <Route
            path="/privacy"
            element={
              <AppShell>
                <Privacy />
              </AppShell>
            }
          />

          {/* Legacy routes - redirect to new structure */}
          <Route path="/title" element={<Navigate to="/game" replace />} />
          <Route path="/options" element={<Navigate to="/game" replace />} />
          <Route path="/onboarding" element={<Navigate to="/game" replace />} />

          {/* Catch all - go to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </TutorialProvider>
          </ChefsLabProvider>
          </AudioSettingsProvider>
          </ProfileProvider>
          </GuestPromptProvider>
        </AuthProvider>
      </UIProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
